import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setAuthToken, setCsrfToken, clearAllTokens } from "./apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (alive) setUser(data);
      } catch (e) {
        if (alive) setUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const login = async (email, password, extra = {}) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, ...extra });
      // Backend setea cookies httpOnly + CSRF. Si el navegador acepta cookies,
      // todo funciona. Si NO (browser strict, política corporativa), guardamos
      // el token y csrf en sessionStorage como fallback.
      if (data.token) setAuthToken(data.token);
      if (data.csrf_token) setCsrfToken(data.csrf_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      // Iter25.f — Email no verificado: el detail viene como objeto.
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "EMAIL_NOT_VERIFIED") {
        return {
          ok: false,
          code: "EMAIL_NOT_VERIFIED",
          email: det.email,
          user_id: det.user_id,
          error: det.message,
        };
      }
      return { ok: false, error: formatApiError(det) || "Error de autenticación" };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      // Iter25.f — Si token vacío, el usuario debe verificar email primero.
      // No autenticamos automáticamente: redirigimos a /verify-email-pending.
      if (!data.token) {
        return { ok: true, pending_verification: true, user: data.user };
      }
      setAuthToken(data.token);
      if (data.csrf_token) setCsrfToken(data.csrf_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || "No se pudo crear la cuenta" };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Si falla (e.g. cookie ya expirada), continuamos con cleanup local igual.
    }
    clearAllTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}

export function dashboardPathForRole(role) {
  if (role === "admin") return "/admin";
  if (role === "reviewer") return "/reviewer";
  return "/dashboard";
}
